<template>
    <div :class="[namespace(), skinStyle]">
        <div :class="namespace('wrapper')" ref="mbg"></div>
    </div>
</template>

<script>
import utils from '../../utils';
import mixins from '../mixins';
import baseMixin from './base-mixin';
import { getDataFormatter, getIconFormatValue } from './data-formatter';
import { MBG } from 'mbg';

export default {
    name: 'markets-ui-mbg',
    mixins: [mixins, baseMixin],
    created() {
        this.namespace = utils.namespace('ui-mbg', false);
        this.visibleRows = [];
        this.throttleResize = utils.throttle(() => {
            if (this.mbg) {
                /* We need check the el offset width must more than 0, because maybe some component
                use keep alive.
                When sub component changed in <keep-alive>, we do not need rerender and set frozen.
                 */
                if (
                    this.$el.offsetWidth > 0 &&
                    this.frozenColumnwidth >= this.$el.offsetWidth - 30
                ) {
                    this.$set(this.columnOptions, 'frozenColumn', -1);
                } else {
                    this.$set(
                        this.columnOptions,
                        'frozenColumn',
                        this.option.frozenColumn
                    );
                }
                this.mbg.resize();
            }
        }, 200);
    },
    mounted() {
        this.mbgContainer = this.$refs.mbg;
        this.createMBG();
        utils.resizeObserver.observe(this.$el, this.throttleResize);
    },
    beforeDestroy() {
        this.mbg.unbind();
        utils.resizeObserver.unobserve(this.$el, this.throttleResize);
    },
    computed: {
        skinStyle() {
            return this.namespace(`__${utils.getSkinSuffix(this.skin)}`);
        },
        visibleOption() {
            return utils.extend(
                {
                    showRowNumber: false,
                    rowHeight: 30,
                    scrollbarSize: 14,
                    removable: false,
                    selectable: true,
                    focusable: false,
                    nullFormat: (
                        v,
                        rowData,
                        columnData,
                        rowIndex,
                        columnIndex
                    ) => {
                        if (rowData.subs && rowData.subs.length) {
                            return columnIndex === 0 ? rowData.group : '';
                        } else {
                            return '—';
                        }
                    }
                },
                this.option,
                this.columnOptions
            );
        }
    },
    data() {
        return {
            allColumns: [],
            allRows: [],
            columnOptions: {},
            sortOption: {}
        };
    },
    watch: {
        scrollTop(value) {
            if (this.mbg) {
                this.mbg.setScrollTop(value);
            }
        },
        scrollLeft(value) {
            if (this.mbg) {
                this.mbg.setScrollLeft(value);
            }
        },
        selectRowIds(value) {
            if (value && value.length) {
                this.setSelectedRows({ rowIds: value });
            }
        },
        focusRowId(value) {
            if (this.mbg && value) {
                this.setFocusedRow({ rowId: value });
            }
        },
        sortColumn: {
            handler(value) {
                this.setSortColumn(value);
            },
            deep: true
        },
        columnWidth(value) {
            if (this.mbg) {
                this.mbg.setColumnWidth(
                    this.mbg.getColumnItemById(value.id).tg_index,
                    value.width
                );
            }
        },
        hoverRow(value) {
            if (this.mbg && value) {
                this.mbg.setHoverRow(value.index, value.hover);
            }
        },
        columns(value) {
            this.setColumns(value);
            this.setRows(this.rows);
        },
        rows: {
            handler(value) {
                this.update(value);
            },
            deep: true
        },
        // fix the group layout bug.
        // you can find the details in
        // https://msstash.morningstar.com/projects/QS/repos/mwc-markets-components/pull-requests/423/overview
        groupKey() {
            this.setRows(this.rows);
        },
        visibleOption() {
            if (this.mbg) {
                const visibleOption = utils.extend(
                    true,
                    {},
                    this.visibleOption,
                    this.getHoverOption()
                );
                this.mbg.setOption(visibleOption);
                this.setColumns(this.columns);
                this.setRows(this.rows);
            }
        }
    },
    methods: {
        setSelectedRows({ rowIds, rowIndexes }) {
            if (this.visibleOption.selectable && this.mbg) {
                // TODO  This will unselectAll rows, if user bind the unselect event it maybe have bug.
                this.mbg.unselectAll();
                if (rowIndexes) {
                    rowIndexes.forEach(index => {
                        this.mbg.setSelectedRow(index);
                    });
                } else if (rowIds) {
                    rowIds.forEach(id => {
                        const row = this.mbg.getRowItemById(id);
                        if (row) {
                            this.mbg.setSelectedRow(row.tg_index);
                        }
                    });
                }
                this.setCheckboxStatus();
                this.setHeaderCheckboxStatus();
            }
        },
        setFocusedRow({ rowId, rowIndex = -1 } = {}) {
            if (
                this.visibleOption.focusable &&
                this.mbg &&
                (rowIndex >= 0 || rowId)
            ) {
                if (rowIndex < 0) {
                    this.focusRow = this.mbg.getRowItemById(rowId);
                    if (this.focusRow) {
                        rowIndex = this.focusRow.tg_index;
                    }
                } else {
                    this.focusRow = this.mbg.getRowItem(rowIndex);
                }
                if (rowIndex >= 0) {
                    // check if focus row is in the visible rows, if not, need to scroll
                    const { rowList } = this.mbg.getVisibleRange();
                    const scroll = utils.inArray(rowList, rowIndex) === -1;
                    if (scroll) {
                        this.mbg.scrollToRow(rowIndex);
                    }
                    this.mbg.setFocusedRow(rowIndex);
                }
            }
        },
        getHoverOption() {
            if (this.computedHover.menu.length) {
                return {
                    treeFormat: getDataFormatter({
                        formatter: this.formatter,
                        dataType: 'tree',
                        namespaced: this.namespaced,
                        namespace: this.namespace,
                        skin: this.skin
                    })
                };
            }
            return null;
        },
        createMBG() {
            this.mbg = new MBG(this.mbgContainer);
            const visibleOption = utils.extend(
                true,
                {},
                this.visibleOption,
                this.getHoverOption()
            );
            this.mbg.setOption(visibleOption);
            const checkboxFormat = getDataFormatter({
                formatter: this.formatter,
                namespaced: this.namespaced,
                namespace: this.namespace,
                skin: this.skin,
                dataType: 'checkbox'
            });
            this.mbg.setFormatter(
                utils.extend(
                    {
                        checkbox: checkboxFormat,
                        header: (value, rowData, columnData) => {
                            if (columnData.id === 'checkbox') {
                                return checkboxFormat();
                            } else {
                                return value;
                            }
                        }
                    },
                    this.visibleOption.formatter
                )
            );

            const _onRenderUpdate = utils.throttle((e, d) => {
                this.setCheckboxStatus();
                this.setHeaderCheckboxStatus();
                const visibleRows = [];
                const addedRows = [];
                const removedRows = [];
                const hidedRows = [];
                const currentAllRowsMap = {};
                const currentVisibleRowsMap = {};
                const latestVisibleRowsMap = {};
                this.mbg.getGridRowsData().forEach(rowItem => {
                    if (this.groupKey) {
                        if (
                            rowItem.tg_group &&
                            rowItem.subs &&
                            rowItem.subs.length
                        ) {
                            rowItem.subs.forEach(groupRowItems => {
                                currentAllRowsMap[groupRowItems.id] = true;
                            });
                        }
                    } else {
                        currentAllRowsMap[rowItem.id] = true;
                    }
                });

                d.rowList.forEach(index => {
                    const rowItem = this.mbg.getRowItem(index);
                    if (!rowItem.tg_group) {
                        currentVisibleRowsMap[rowItem.id] = true;
                        visibleRows.push(rowItem);
                    }
                });

                // get removed/hide rows
                this.visibleRows.forEach(rowItem => {
                    latestVisibleRowsMap[rowItem.id] = true;
                    if (!currentAllRowsMap[rowItem.id]) {
                        removedRows.push(rowItem);
                    } else if (!currentVisibleRowsMap[rowItem.id]) {
                        hidedRows.push(rowItem);
                    }
                });

                visibleRows.forEach(rowItem => {
                    if (!latestVisibleRowsMap[rowItem.id]) {
                        addedRows.push(rowItem);
                    }
                });

                this.visibleRows = visibleRows;

                if (
                    addedRows.length ||
                    removedRows.length ||
                    hidedRows.length
                ) {
                    this.$emit('visible-rows-update', {
                        visibleRows,
                        addedRows,
                        removedRows,
                        hidedRows
                    });
                }
            }, this.visibleOption.visibleRowsUpdateWaitTime);

            const _onScrollStatusChanged = utils.throttle((e, d) => {
                this.$emit('scroll-status-changed', e, d);
            }, this.visibleOption.visibleRowsUpdateWaitTime);

            this.mbg
                .bind(
                    'onDblClick',
                    function(e, d) {
                        const rowData = this.mbg.getRowItem(d.row);
                        this.setFocusedRow({ rowIndex: d.row });
                        if (rowData) {
                            this.$emit('row-double-click', {
                                row: rowData,
                                e: d.e
                            });
                        }
                    }.bind(this)
                )
                .bind(
                    'onClick',
                    function(e, d) {
                        const rowData = this.mbg.getRowItem(d.row);
                        this.setFocusedRow({ rowIndex: d.row });
                        const target = d.e.target;
                        const checkbox = d.cellNode.querySelector(
                            'markets-ui-checkbox .markets-ui-checkbox-input'
                        );
                        if (
                            target.classList.contains(
                                this.namespace('__button--icon-only-remove')
                            )
                        ) {
                            this.removeRows(d.row);
                        } else if (checkbox) {
                            this.checkboxClicked(checkbox, e.data.row);
                        } else if (
                            target.classList.contains(
                                this.namespace('__button--icon-only')
                            )
                        ) {
                            this.$emit('icon-click', {
                                element: target,
                                row: rowData,
                                e: d.e.originalEvent
                            });
                        }
                        this.$emit('cell-click', {
                            row: rowData,
                            column: this.mbg.getColumnItem(d.column),
                            e: d.e.originalEvent
                        });
                        this.$emit('row-click', {
                            row: rowData,
                            e: d.e.originalEvent
                        });
                    }.bind(this)
                )
                .bind('onRenderUpdate', _onRenderUpdate)
                .bind(
                    'onSort',
                    function(e, d) {
                        this.sortOption = d.columnItem;
                    }.bind(this)
                )
                .bind(
                    'onRenderComplete',
                    function(e, d) {
                        this.groupScrollHeader = this.mbg.gridView.$paneHeader.find(
                            '.tg-column-group-name'
                        );
                        this.groupSrollBody = this.mbg.gridView.$paneHeader.find(
                            '.tg-scrollbody'
                        ).list[this.visibleOption.frozenColumn >= 0 ? 1 : 0];
                        const frozenColumn = this.mbg.columns.filter(
                            item => item.tg_frozen
                        );
                        if (frozenColumn.length) {
                            let frozenColumnwidth = 0;
                            frozenColumn.forEach(d => {
                                frozenColumnwidth += d.width;
                            });
                            this.frozenColumnwidth = frozenColumnwidth;
                        }
                        // Reset grid state.
                        this.setSelectedRows({ rowIds: this.selectRowIds });
                        this.setFocusedRow({ rowId: this.focusRowId });
                        this.setSortColumn(this.sortColumn);

                        this.$emit('render-complete', d);
                        this.throttleResize();
                    }.bind(this)
                )
                .bind('onScrollStatusChanged', _onScrollStatusChanged)
                .bind(
                    'onSelectedChanged',
                    function(e, d) {
                        const selectedRows = this.mbg.getSelectedRows();
                        this.$emit('rows-selected-changed', selectedRows);
                    }.bind(this)
                )
                .bind(
                    'onHeaderClick',
                    function(data) {
                        const { node } = data.data;
                        const checkbox = node.querySelector(
                            'markets-ui-checkbox .markets-ui-checkbox-input'
                        );
                        if (checkbox) {
                            this.checkboxClicked(checkbox, undefined, 'all');
                        }
                    }.bind(this)
                )
                .bind(
                    'onRowMouseEnter',
                    function(d) {
                        this.handleRowMouseEvent('row-mouse-enter', d);
                    }.bind(this)
                )
                .bind(
                    'onRowMouseLeave',
                    function(d) {
                        this.handleRowMouseEvent('row-mouse-leave', d);
                    }.bind(this)
                )
                .bind(
                    'onCellMouseEnter',
                    function(d) {
                        this.handleCellMouseEvent('cell-mouse-enter', d);
                    }.bind(this)
                )
                .bind(
                    'onCellMouseLeave',
                    function(d) {
                        this.handleCellMouseEvent('cell-mouse-leave', d);
                    }.bind(this)
                )
                .bind(
                    'onScroll',
                    function({ data }) {
                        const left =
                            parseInt(this.groupSrollBody.style.left, 10) * -1;
                        if (left >= 0) {
                            this.groupScrollHeader.css({
                                'padding-left': left
                            });
                        }
                        const lastRowIndex =
                            this.mbg.getGridRowsData().length - 1;

                        const islastRowRendered =
                            this.mbg.getRowNodes(lastRowIndex) !== null;

                        this.$emit(
                            'scroll',
                            utils.extend(true, data, {
                                isLastRowShow: islastRowRendered
                            })
                        );
                    }.bind(this)
                )
                .bind(
                    'onSort',
                    function({ data }) {
                        this.$emit('sort', {
                            e: data.e.originalEvent,
                            column: data.columnItem
                        });
                    }.bind(this)
                )
                .bind(
                    'onColumnWidthChanged',
                    function({ data }) {
                        this.$emit('column-width-changed', {
                            column: data
                        });
                    }.bind(this)
                );
            this.setColumns(this.columns);
            this.setRows(this.rows);
        },
        handleRowMouseEvent(event, { data }) {
            this.$emit(event, {
                row: data.rowItem,
                e: data.e.originalEvent
            });
        },
        handleCellMouseEvent(event, { data }) {
            this.$emit(event, {
                row: this.mbg.getRowItem(data.row),
                column: this.mbg.getColumnItem(data.column),
                e: data.e.originalEvent
            });
        },
        render() {
            if (this.mbg) {
                this.mbg.setData({
                    columns: this.allColumns,
                    rows: this.allRows
                });
                this.mbg.render();
            }
        },
        fillFormatter(column) {
            if (column.subs && column.subs.length) {
                column.subs.forEach(col => {
                    this.fillFormatter(col);
                });
            } else {
                if (
                    !column.formatter &&
                    column.dataType !== 'checkbox' &&
                    column.dataType !== 'tree'
                ) {
                    column.formatter = getDataFormatter({
                        formatter: this.formatter,
                        dataType: column.dataType,
                        namespaced: this.namespaced,
                        namespace: this.namespace,
                        skin: this.skin
                    });
                }
            }
        },
        setColumns(data) {
            const allColumns = data.map(column => {
                const newCol = utils.extend(true, {}, column);
                this.fillFormatter(newCol);
                return newCol;
            });
            if (this.option.removable) {
                allColumns.push({
                    id: 'remove',
                    name: '',
                    width: 50,
                    headerClass: 'markets-ui-grid__column--remove',
                    align: 'center',
                    sortable: false,
                    formatter: (value, rowData) => {
                        if (rowData.subs && rowData.subs.length) {
                            return '';
                        }
                        return getIconFormatValue({
                            namespaced: this.namespaced,
                            cls: `${this.namespace(
                                '__button--icon-only'
                            )} ${this.namespace(
                                '__button--small'
                            )} ${this.namespace('__button--icon-only-remove')}`,
                            svgCls: this.namespace('__icon')
                        })('#remove--s');
                    }
                });
            }
            this.allColumns = allColumns;
        },
        _getFlatAllRows() {
            if (this.mbg.getGridRowsData().length === 0) {
                return [];
            }
            if (this.groupKey) {
                const rows = [];
                this.allRows.forEach(r => {
                    if (r.subs && r.subs.length) {
                        r.subs.forEach(sr => {
                            rows.push(sr);
                        });
                    } else {
                        rows.push(r);
                    }
                });
                return rows;
            } else {
                return this.allRows;
            }
        },
        update(rows) {
            const oldRows = this.cRows || [];
            // rowClass is for options
            if (this.same(rows, oldRows, ['id', 'rowClass'])) {
                this.updateRows(rows);
                this.setRows(rows, false);
            } else {
                this.setRows(rows);
            }
        },
        setRows(data, render = true) {
            const allRows = utils.extend(true, [], data);
            if (this.groupKey) {
                this.allRows = this._getGroupRows(allRows);
            } else {
                this.allRows = allRows;
            }
            this.cRows = allRows.map(row => {
                return {
                    id: row.id,
                    rowClass: row.rowClass
                };
            });
            if (render) {
                this.render();
            }
        },
        updateRows(data = []) {
            if (!this.mbg) {
                return;
            }
            data.forEach(d => {
                const rowItem = this.mbg.getRowItemById(d.id);
                if (rowItem) {
                    for (const colId in d) {
                        const columnItem = this.mbg.getColumnItemById(colId);
                        if (columnItem && d[colId] !== rowItem[colId]) {
                            if (columnItem.flash && !d.isEmpty) {
                                this._flashCell(
                                    rowItem,
                                    columnItem,
                                    d[colId],
                                    rowItem[colId]
                                );
                            }
                            this.mbg.updateCell(
                                rowItem.tg_index,
                                columnItem.tg_index,
                                d[colId]
                            );
                        }
                    }
                }
            });
        },
        removeRows(data) {
            let rowIndexList = [];
            const groups = {};
            if (utils.isArray(data)) {
                data.forEach(d => {
                    const rowItem = this.mbg.getRowItemById(d.id);
                    if (rowItem) {
                        rowIndexList.push(rowItem.tg_index);
                        if (this.groupKey) {
                            groups[rowItem.group] = true;
                        }
                    }
                });
            } else {
                rowIndexList.push(data);
                if (this.groupKey) {
                    const rowItem = this.mbg.getRowItem(data);
                    groups[rowItem.group] = true;
                }
            }

            if (rowIndexList.length) {
                this.mbg.deleteRows(rowIndexList);
            }

            //start to delete group rows if there is no sub rows.
            rowIndexList = [];
            const groupKeys = Object.keys(groups);
            groupKeys.forEach(group => {
                const groupRowItem = this.mbg.getRowItemById(group);
                if (!groupRowItem.subs || groupRowItem.subs.length === 0) {
                    rowIndexList.push(groupRowItem.tg_index);
                }
            });
            if (rowIndexList.length) {
                this.mbg.deleteRows(rowIndexList);
            }
        },
        _flashCell(rowItem, columnItem, newValue, oldValue) {
            const cellNode = this.mbg.getCellNode(
                rowItem.tg_index,
                columnItem.tg_index
            );
            const flashClass = this.getFlashClass(
                columnItem,
                newValue,
                oldValue
            );
            if (flashClass && cellNode) {
                utils.flash(cellNode, flashClass);
            }
        },
        _getGroupRows(allRows) {
            const groupRows = [];
            const map = {};
            allRows.forEach(row => {
                const group = row[this.groupKey] || 'Undefined';
                if (group) {
                    map[group] = map[group] || [];
                    map[group].push(row);
                }
            });
            for (const group in map) {
                groupRows.push({
                    id: group,
                    group,
                    subs: map[group]
                });
            }
            return groupRows;
        },
        // showPopoverTips(target, rowData) {
        //     this.popoverTips.target = target;
        //     this.popoverTips.visible = true;
        //     this.popoverTips.tipsMsg = rowData.tipsMsg;
        //     this.popoverTips.customClass = rowData.customClass;
        // },
        // hidePopoverTips() {
        //     this.popoverTips.visible = false;
        // },
        checkboxClicked(checkbox, rowIndex, type) {
            this.$nextTick(() => {
                if (checkbox.checked) {
                    if (type === 'all') {
                        this.mbg.selectAll();
                        this.setCheckboxStatus();
                    } else {
                        this.mbg.setSelectedRow(rowIndex);
                    }
                } else {
                    if (type === 'all') {
                        this.mbg.unselectAll();
                        this.setCheckboxStatus();
                    } else {
                        this.mbg.unsetSelectedRow(rowIndex);
                    }
                }
                this.setHeaderCheckboxStatus();
                const selectedRows = this.mbg.getSelectedRows();
                this.$emit('rows-selected-changed', selectedRows);
            });
        },
        setHeaderCheckboxStatus() {
            const selectedRows = this.mbg.getSelectedRows();
            const headerCheckbox = this.$el.querySelector(
                '.tg-pane-header .markets-ui-checkbox-input'
            );
            if (headerCheckbox) {
                const selectedCount = selectedRows.length;
                if (selectedCount === 0) {
                    headerCheckbox.checked = false;
                    headerCheckbox.indeterminate = false;
                } else {
                    const rowsCount = this.allRows.length;
                    if (selectedCount === rowsCount) {
                        headerCheckbox.checked = true;
                        headerCheckbox.indeterminate = false;
                    } else {
                        headerCheckbox.checked = false;
                        headerCheckbox.indeterminate = true;
                    }
                }
            }
        },
        setCheckboxStatus() {
            const selectedRows = this.mbg.getSelectedRows();
            const allCheckbox = this.$el.querySelectorAll(
                '.markets-ui-checkbox-input'
            );
            if (allCheckbox && allCheckbox.length) {
                const map = {};
                selectedRows.forEach(row => {
                    map[row.id] = true;
                });
                for (let i = 0; i < allCheckbox.length; i++) {
                    allCheckbox[i].checked = !!map[allCheckbox[i].value];
                }
            }
        },
        setSortColumn(value) {
            if (this.mbg && value) {
                const column = this.mbg.getSortColumn(value.id);
                if (column) {
                    column.sortAsc = value.order === 'ascending' ? true : false;
                    this.mbg.setSortColumn(column);
                    if (value.clearStatus) {
                        this.mbg.removeSortStatus();
                    }
                }
            }
        }
    }
};
</script>
<style lang="scss">
@import '../../mds/constants';
@import 'common';
$namespace: 'markets-ui-mbg';
.#{$namespace} {
    height: 100%;
    font-family: $mds-font-family-sans-serif;
    font-weight: $mds-font-weight-light;
    color: $mds-text-color-primary-on-light;
    &-wrapper {
        height: 100%;
    }
    &__column--remove {
        height: 100%;
        .tg-column-name {
            height: 100%;
        }
    }
    &__button--icon-only {
        cursor: pointer;
        display: inline-block;
        &-hover {
            position: absolute;
            top: 6px;
            right: 0;
            display: none;
        }
    }
    &__icon--search {
        fill: $mds-color-neutral-12;
        margin: 0;
        padding: 0;
        position: absolute;
        top: 50%;
        right: 7px;
        -webkit-transform: translateY(-50%);
        transform: translateY(-50%);
        width: 15px;
    }
    &__input {
        border: none;
        height: 22px;
        font-size: 14px;
        border-bottom: 1px solid $mds-color-neutral-95;
        background: $mds-color-neutral-90;
        font-family: $mds-font-family-sans-serif;
        font-weight: $mds-font-weight-light;
        color: $mds-text-color-primary-on-light;
        padding-right: 20px;
    }

    .tg-hover &__button--icon-only {
        &-hover {
            display: inherit;
        }
    }

    .tg-mbg .tg-header-column[data='checkbox'] .tg-column-name {
        padding-bottom: 2px;
    }
    .tg-mbg .tg-cell {
        border-right: none;
    }
    .tg-mbg
        .tg-header-table-sortable
        .tg-header-column[data='checkbox']
        .tg-column-name {
        padding-bottom: 16px;
    }
    .tg-cell,
    .tg-column-name {
        .markets-ui-checkbox {
            margin-top: 7px;
            display: flex;
            justify-content: center;
        }
    }

    .tg-mbg .tg-row.tg-selected,
    .tg-mbg .tg-row.tg-hover,
    .tg-mbg .tg-row.tg-selected.tg-hover {
        @include hover;
    }

    .tg-mbg .tg-row.tg-focused {
        border-bottom-color: $mds-interactive-color-primary-hover;
    }

    &__cell--flash {
        opacity: 0.6;
        &-positive {
            @include flash(positive);
        }
        &-negative {
            @include flash(negative);
        }
    }

    &__negative {
        @include performance(negative);
    }
    &__neutral {
        @include performance();
    }
    &__positive {
        @include performance(positive);
    }

    &__dark-gray {
        background: $mds-background-color-dark-gray;
        .tg-mbg {
            .tg-scrollbar-track {
                background: $mds-background-color-dark-gray;
            }
            .tg-header-table {
                td {
                    color: $mds-text-color-secondary-on-dark;
                }
            }
            .tg-row.tg-selected {
                background: $mds-color-neutral-20;
                &.tg-hover {
                    background: $mds-color-neutral-20;
                }
            }

            .tg-column-name {
                color: $mds-text-color-secondary-on-dark;
                border-right-color: $mds-background-color-dark-gray;
            }
            .tg-column-group-name {
                color: $mds-text-color-secondary-on-dark;
                border-bottom-color: $mds-border-separator-on-dark-gray;
            }
            .tg-header-column-sorted {
                color: $mds-text-color-secondary-on-dark;
                .tg-tree-icon-all .tg-icon-item {
                    fill: $mds-text-color-secondary-on-dark;
                }
            }
            .tg-sort-indicator {
                border-bottom: $mds-border-container-on-all-backgrounds;
            }
            .tg-sort-indicator-icon .tg-icon-item {
                fill: $mds-color-neutral-90;
            }

            .tg-column-line-item {
                &.tg-hover {
                    background-color: $mds-color-neutral-20;
                }
                &.tg-active {
                    background-color: $mds-interactive-color-primary-active;
                }
            }

            .tg-tree-row-number {
                color: $mds-text-color-secondary-on-dark;
            }

            .tg-tree-icon .tg-icon-item {
                fill: $mds-text-color-primary-on-dark;
            }

            .tg-tree-icon-all .tg-icon-item {
                fill: #666;
            }

            .tg-row {
                border-top-color: $mds-color-neutral-20;
                &.tg-group {
                    border-top-color: $mds-color-neutral-90;
                }
                &.tg-hover {
                    background: $mds-color-neutral-20;
                    .#{$namespace}__button--icon-only {
                        svg {
                            fill: $mds-text-color-primary-on-dark;
                        }
                    }
                }
                svg {
                    fill: $mds-text-color-primary-on-dark;
                }
            }
            .tg-cell {
                border-right-color: $mds-background-color-dark-gray;
                color: $mds-text-color-primary-on-dark;
            }
        }
        .#{$namespace} {
            &__cell--flash {
                &-positive {
                    @include flash(positive, dark);
                    background-color: $mds-text-color-performance-positive-on-dark;
                }
                &-negative {
                    @include flash(negative, dark);
                }
            }
            &__negative {
                @include performance(negative, dark);
            }
            &__neutral {
                @include performance(neutral, dark);
            }
            &__positive {
                @include performance(positive, dark);
            }

            &__input {
                background: $mds-color-neutral-20;
                color: $mds-color-neutral-90;
            }
            &__icon--search {
                fill: $mds-color-neutral-95;
            }
        }
    }
}
</style>
